import { Component, Inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormGroup, ReactiveFormsModule } from '@angular/forms';
import { FormlyFieldConfig, FormlyModule } from '@ngx-formly/core';
import { FormlyMaterialModule } from '@ngx-formly/material';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';

export interface CustomBankAccountDialogData {
  loanNumber: string;
  payeeName: string;
}

export interface CustomBankAccount {
  institutionCode: string;
  transitNumber: string;
  accountNumber: string;
}

/** Collects a bank account that is not in the customer's list, for one manual row only. */
@Component({
  selector: 'app-custom-bank-account-dialog',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    FormlyModule,
    FormlyMaterialModule,
    MatDialogModule,
    MatButtonModule,
    MatIconModule,
    MatTooltipModule,
  ],
  templateUrl: './custom-bank-account-dialog.component.html',
  styleUrls: ['./custom-bank-account-dialog.component.scss'],
})
export class CustomBankAccountDialogComponent {
  readonly form = new FormGroup({});
  readonly model: CustomBankAccount = { institutionCode: '', transitNumber: '', accountNumber: '' };

  // Same rules as the transaction edit dialog.
  readonly fields: FormlyFieldConfig[] = [
    {
      fieldGroupClassName: 'row',
      fieldGroup: [
        {
          className: 'col-6',
          key: 'institutionCode',
          type: 'input',
          props: {
            label: 'Institution Code',
            required: true,
            maxLength: 3,
            pattern: /^\d{3}$/,
            appearance: 'outline',
            attributes: { autocomplete: 'off', inputmode: 'numeric' },
          },
          validation: {
            messages: { pattern: 'Institution code must be exactly 3 digits' },
          },
        },
        {
          className: 'col-6',
          key: 'transitNumber',
          type: 'input',
          props: {
            label: 'Transit Number',
            required: true,
            maxLength: 5,
            pattern: /^\d{5}$/,
            appearance: 'outline',
            attributes: { autocomplete: 'off', inputmode: 'numeric' },
          },
          validation: {
            messages: { pattern: 'Transit number must be exactly 5 digits' },
          },
        },
        {
          className: 'col-12',
          key: 'accountNumber',
          type: 'input',
          props: {
            label: 'Account Number',
            required: true,
            maxLength: 20,
            pattern: /^\d{1,20}$/,
            appearance: 'outline',
            attributes: { autocomplete: 'off', inputmode: 'numeric' },
          },
          validation: {
            messages: { pattern: 'Account number must be 1-20 digits' },
          },
        },
      ],
    },
  ];

  constructor(
    private readonly dialogRef: MatDialogRef<CustomBankAccountDialogComponent, CustomBankAccount>,
    @Inject(MAT_DIALOG_DATA) readonly data: CustomBankAccountDialogData,
  ) {}

  onSave(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }
    this.dialogRef.close({
      institutionCode: this.model.institutionCode.trim(),
      transitNumber: this.model.transitNumber.trim(),
      accountNumber: this.model.accountNumber.trim(),
    });
  }
}
